'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Users } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Alert } from '@/components/common/Alert';
import { BottomSheet } from '@/components/common/BottomSheet';
import type { Player, Pool } from '@/lib/types/auction';

const poolChangeSchema = z.object({
  poolId: z.string().min(1, 'Please select a pool'),
});

type PoolChangeInput = z.infer<typeof poolChangeSchema>;

interface PoolChangeModalProps {
  player: Player;
  availablePools: Pool[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (poolId: string) => Promise<void>;
}

export function PoolChangeModal({
  player,
  availablePools,
  isOpen,
  onClose,
  onSave,
}: PoolChangeModalProps) {
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PoolChangeInput>({
    resolver: zodResolver(poolChangeSchema),
    defaultValues: {
      poolId: player.pool?.id || '',
    },
  });

  const onSubmit = async (data: PoolChangeInput) => {
    try {
      setError(null);
      await onSave(data.poolId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change pool');
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Change Player Pool"
      className="sm:max-w-md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && <Alert type="error" message={error} />}

        <div className="space-y-1">
          <label
            htmlFor="poolId"
            className="block text-sm font-medium text-gray-700"
          >
            Select Pool
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Users className="h-5 w-5 text-gray-400" />
            </div>
            <select
              id="poolId"
              className={`block w-full rounded-lg border-gray-300 pl-10 focus:border-primary-500 focus:ring-primary-500 ${
                errors.poolId ? 'border-red-300' : ''
              }`}
              {...register('poolId')}
            >
              <option value="">Select a pool</option>
              {availablePools.map((pool) => (
                <option key={pool.id} value={pool.id}>
                  {pool.name}
                </option>
              ))}
            </select>
          </div>
          {errors.poolId && (
            <p className="mt-1 text-xs text-red-600">{errors.poolId.message}</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-3">
          <Button variant="light" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Change Pool
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}
